import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Check, CheckCircle, CleaningServices, Search as SearchIcon } from "@mui/icons-material";
// Reemplaza esta ruta por la de tu proyecto
import CleanningButton from "../../components/CleanningButton";
import CustomSnackBar from "../../components/CustomSnackBar";

export default function AllocationModalContent({
  onSave,
  invoices = [],
  amountAvailable = 0,
  initial = {},
  formatCurrency = (n) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "GTQ" }).format(n ?? 0),
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [local, setLocal] = useState(() => ({ ...(initial || {}) }));
  const [localInput, setLocalInput] = useState(() => {
    const seed = {};
    Object.entries(initial || {}).forEach(([k, v]) => {
      const num = Number(v);
      seed[k] = Number.isFinite(num) && num > 0 ? num.toFixed(2) : "";
    });
    return seed;
  });
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  // ---- helpers numéricos ----
  const getDue = useCallback((inv) => {
    const raw = typeof inv?.netDue === "number"
      ? inv.netDue
      : typeof inv?.due === "number"
        ? inv.due
        : inv?.payableSaldoNum ?? inv?.totalNum ?? 0;
    return Math.max(0, Number(raw) || 0);
  }, []);

  const totalAssigned = useMemo(
    () => Object.values(localInput).reduce((s, v) => s + (Number(v) || 0), 0),
    [localInput]
  );

  const remainingGlobal = useMemo(
    () => Math.max(0, (Number(amountAvailable) || 0) - totalAssigned),
    [amountAvailable, totalAssigned]
  );

  // Máximo asignable por factura considerando su due y el pool restante
  const maxAssignableFor = useCallback(
    (invId) => {
      const inv = invoices.find((x) => String(x.id) === String(invId));
      const due = getDue(inv);
      const current = Number(localInput[invId] || 0);
      const pool = (Number(amountAvailable) || 0) - (totalAssigned - current);
      return Math.max(0, Math.min(due, pool));
    },
    [amountAvailable, totalAssigned, invoices, localInput, getDue]
  );

  const clampValue = useCallback(
    (invId, raw) => {
      const num = Math.max(0, Number(String(raw).replace(/[^0-9.-]/g, "") || 0));
      const max = maxAssignableFor(invId);
      const clamped = Math.min(num, max);
      return Math.round(clamped * 100) / 100;
    },
    [maxAssignableFor]
  );

  const setValue = useCallback(
    (invId, raw) => {
      const next = clampValue(invId, raw);
      setLocal((prev) => ({ ...prev, [invId]: next }));
      setLocalInput((prev) => ({ ...prev, [invId]: next > 0 ? Number(next).toFixed(2) : "" }));
    },
    [clampValue]
  );

  const clearValue = useCallback((invId) => {
    setLocal((prev) => ({ ...prev, [invId]: 0 }));
    setLocalInput((prev) => ({ ...prev, [invId]: "" }));
  }, []);

  const canShowFullCheck = useCallback(
    (invId) => {
      const inv = invoices.find((x) => String(x.id) === String(invId));
      if (!inv) return false;
      const due = getDue(inv);
      const current = Number(local[invId] || 0);
      const need = Math.max(0, due - current);
      return remainingGlobal >= need && need > 0;
    },
    [invoices, remainingGlobal, local, getDue]
  );

  const assignFull = useCallback(
    (invId) => {
      const inv = invoices.find((x) => String(x.id) === String(invId));
      if (!inv) return;
      const due = getDue(inv);
      setLocal((prev) => ({ ...prev, [invId]: Math.round(due * 100) / 100 }));
      setLocalInput((prev) => ({ ...prev, [invId]: Number(due).toFixed(2) }));
    },
    [invoices, getDue]
  );

  const progress = useMemo(() => {
    const avail = Number(amountAvailable) || 0;
    if (avail <= 0) return 0;
    return Math.min(100, Math.max(0, (totalAssigned / avail) * 100));
  }, [amountAvailable, totalAssigned]);

  const hasAnyValue = useMemo(
    () => Object.values(localInput).some((v) => Number(v) > 0),
    [localInput]
  );

  const items = useMemo(() => {
    const copy = [...invoices];
    copy.sort((a, b) => (getDue(b) - getDue(a)) || String(a.number).localeCompare(String(b.number)));
    return copy;
  }, [invoices, getDue]);

  const norm = useCallback((s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim(),
    []);

  const filtered = useMemo(() => {
    const q = norm(search);
    if (!q) return items;
    return items.filter((r) => {
      const hay = [r.number, r.code, r.description, r.name]
        .map(norm)
        .join(' ');
      return hay.includes(q);
    });
  }, [items, search, norm]);
  const handleConfirm = () => {
    const next = {};
    Object.entries(localInput).forEach(([invId, raw]) => {
      const value = clampValue(invId, raw);
      if (value > 0) next[invId] = value;
    });
    setLocal(next);
    setLocalInput(
      Object.fromEntries(
        Object.entries(next).map(([k, v]) => [k, Number(v).toFixed(2)])
      )
    );
    setSnack({ open: true, message: 'Cantidades asignadas', severity: 'success' });
    onSave(next)

  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Encabezado */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5, gap: 1 }}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Distribuye el monto</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Abono a distribuir: <b>{formatCurrency(amountAvailable)}</b>
          </Typography>
        </Stack>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 280 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 999 }} />
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Typography variant="caption">Asignado: {formatCurrency(totalAssigned)}</Typography>
            <Typography variant="caption">Restante: {formatCurrency(remainingGlobal)}</Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Buscador full-width, simétrico */}
      <TextField
        size="small"
        fullWidth
        placeholder="Buscar por código o descripción"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
      />

      <Divider />

      {/* Lista */}
      <Box sx={{ maxHeight: { xs: 360, sm: 420 }, overflow: "auto", pr: 0.5 }}>
        <Stack spacing={0.75}>
          {filtered.map((r) => {
            const invId = r.id;
            const due = getDue(r);
            const current = Number(localInput[invId] || 0);
            const maxForThis = maxAssignableFor(invId);
            const remainingForThis = Math.max(0, due - current);
            const showFull = canShowFullCheck(invId);
            const disableInput = maxForThis <= 0 && current <= 0; // sin pool y sin valor actual

            return (
              <Box
                key={invId}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(96px,auto) 96px 1fr",
                    sm: "minmax(120px,auto) 110px 1fr",
                  },
                  alignItems: "center",
                  gap: 0.5,
                  p: 0.75,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
                }}
              >
                {/* Col 1: Número */}
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  #{r.number}
                </Typography>

                {/* Col 2: Saldo */}
                <Typography variant="body2" sx={{ textAlign: "right", whiteSpace: 'nowrap' }}>
                  {formatCurrency(due)}
                </Typography>

                {/* Col 3: Input + acciones + check dentro del cuadro */}
                <Stack spacing={0.375} sx={{ width: "100%" }}>
                  {/* Fila superior: input + clean + check */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ width: "100%", flexWrap: 'wrap', columnGap: 0.5, rowGap: 0.5 }}
                  >
                    <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
                      <TextField
                        label="Monto"
                        name={`amount-${invId}`}
                        required
                        variant="standard"
                        inputMode="decimal"
                        value={localInput[invId] ?? ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(",", ".");
                          const isValid = /^\d*(\.\d{0,2})?$/.test(raw);
                          if (raw === "" || isValid) {
                            setLocalInput((prev) => ({ ...prev, [invId]: raw }));
                          }
                        }}
                        onBlur={() => setValue(invId, localInput[invId] ?? "")}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        sx={{ pointerEvents: disableInput ? 'none' : 'auto', opacity: disableInput ? 0.55 : 1 }}
                      />
                    </Box>

                    <CleanningButton
                      onClick={() => clearValue(invId)}
                      tooltipText="Limpiar"
                      iconColor="primary.main"
                      paddingBottom="0px"
                      disabled={current <= 0}
                    />

                    {showFull && (
                      <FormControlLabel
                        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem', ml: 0.25 }, '& .MuiCheckbox-root': { p: 0.25 }, flex: '0 0 auto' }}
                        control={
                          <Checkbox
                            size="small"
                            onChange={(e) => { if (e.target.checked) assignFull(invId); else clearValue(invId); }}
                            checked={Math.abs(current - due) < 0.01}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={0.25} alignItems="center">
                            <CheckCircle sx={{ fontSize: 14 }} />
                            {!isXs && <Typography variant="caption">Completo</Typography>}
                          </Stack>
                        }
                      />
                    )}
                  </Stack>

                  {/* Fila inferior: helper derecha */}
                  <Typography variant="caption" sx={{ opacity: 0.8, textAlign: "right" }}>
                    Saldo: <b>{formatCurrency(remainingForThis)}</b> • Máx: {formatCurrency(maxForThis)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Divider />

      {/* Footer */}
      <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          * Los importes se ajustan automáticamente para no exceder el abono disponible ni el saldo neto de cada factura.
        </Typography>
        <Stack direction="row" gap={0.75} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<CleaningServices fontSize="inherit" />}
            disabled={!hasAnyValue && totalAssigned === 0}
            onClick={() => setLocal({})}
            disableElevation
            sx={{ textTransform: 'none', borderRadius: 1.5, px: 1.1, py: 0.15, fontSize: 12, minHeight: 15, '& .MuiButton-startIcon': { m: 0 } }}
          >
            Limpiar todo
          </Button>

          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<Check fontSize="inherit" />}
            onClick={handleConfirm}
            disableElevation
            sx={{ textTransform: 'none', borderRadius: 1.5, px: 1.2, py: 0.15, fontSize: 12, minHeight: 15, '& .MuiButton-startIcon': { m: 0 } }}
          >
            Confirmar
          </Button>
        </Stack>
      </Stack>
      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}
